import { normalizeManifestI18n } from "../../intl/manifestI18n";
import { normalizeI18nRollout } from "../../intl/i18nRollout.js";
import { AUTOMAPA_CLIENT_VIEW_SLUG } from "../../constants/seoPageConstants";

export const AUTOCLARO_SLEEK_HOME_LAYOUT = "autoclaro-sleek";

export const AUTOMAPA_RUNTIME_I18N = {
  defaultLocale: "es-CO",
  supportedLocales: ["es-CO", "en-US", "ru-RU"],
  defaultCurrency: "COP",
  supportedCurrencies: ["COP", "USD", "EUR", "RUB"],
};

export const AUTOMAPA_I18N_ROLLOUT = {
  clientViewI18nEnabled: true,
  localizedRoutesEnabled: true,
  localizedSeoEnabled: true,
  displayCurrencyEnabled: true,
  fxConversionEnabled: true,
  studioTranslationEditorEnabled: true,
  translationImportEnabled: true,
  missingTranslationWarningsEnabled: true,
};

/** Default GA4 property for AutoMapa public Client View. */
export const AUTOMAPA_GA4_MEASUREMENT_ID = "G-1PJJY3WBC0";
export const AUTOMAPA_GA4_ENV_KEY = "REACT_APP_AUTOMAPA_GA4_ID";

function uniqueMergeList(preferred, fallback) {
  const seen = new Set();
  const result = [];
  for (const value of [...(preferred || []), ...(fallback || [])]) {
    if (!value || seen.has(value)) continue;
    seen.add(value);
    result.push(value);
  }
  return result;
}

/**
 * Upgrade thin manifest lists to AutoMapa runtime defaults.
 * When the operator configured two or more explicit values, treat the manifest as authoritative
 * so ru-RU / RUB can be rolled back without client enrichment re-adding them.
 */
function mergeRuntimeSupportedList(manifestList, runtimeDefaults) {
  if (Array.isArray(manifestList) && manifestList.length >= 2) {
    return manifestList;
  }
  return uniqueMergeList(manifestList, runtimeDefaults);
}

export function isAutoMapaExperienceManifest(manifest, slug = null) {
  if (slug === AUTOMAPA_CLIENT_VIEW_SLUG) {
    return true;
  }
  const homePage = (manifest?.pages || []).find((page) => page.template === "home");
  if (homePage?.content?.homeLayout === AUTOCLARO_SLEEK_HOME_LAYOUT) {
    return true;
  }
  return (manifest?.dataSources || []).some(
    (source) => source.type === "vehicle_market" && source.enabled !== false
  );
}

export function enrichAutoMapaManifest(manifest, slug = null) {
  if (!manifest || !isAutoMapaExperienceManifest(manifest, slug)) {
    return manifest;
  }

  const current = manifest.i18n || {};
  const mergedRaw = {
    ...AUTOMAPA_RUNTIME_I18N,
    ...current,
    supportedLocales: mergeRuntimeSupportedList(
      current.supportedLocales,
      AUTOMAPA_RUNTIME_I18N.supportedLocales
    ),
    supportedCurrencies: mergeRuntimeSupportedList(
      current.supportedCurrencies,
      AUTOMAPA_RUNTIME_I18N.supportedCurrencies
    ),
  };

  const mergedI18n = normalizeManifestI18n(mergedRaw);
  const rollout = normalizeI18nRollout(
    {
      ...AUTOMAPA_I18N_ROLLOUT,
      ...(current.rollout || {}),
    },
    mergedI18n
  );

  const pages = (manifest.pages || []).map((page) => {
    if (page.template !== "home") {
      return page;
    }
    return {
      ...page,
      content: {
        ...(page.content || {}),
        homeLayout: page.content?.homeLayout || AUTOCLARO_SLEEK_HOME_LAYOUT,
      },
    };
  });

  const existingAnalytics = manifest.analytics || {};
  const existingGa = existingAnalytics.googleAnalytics || {};
  const measurementId =
    (typeof existingGa.measurementId === "string" && existingGa.measurementId.trim()) ||
    AUTOMAPA_GA4_MEASUREMENT_ID;

  return {
    ...manifest,
    pages,
    analytics: {
      ...existingAnalytics,
      enabled: existingAnalytics.enabled !== false,
      clientViewCode: existingAnalytics.clientViewCode || AUTOMAPA_CLIENT_VIEW_SLUG,
      googleAnalytics: {
        ...existingGa,
        enabled: true,
        measurementId,
        measurementIdEnvKey: existingGa.measurementIdEnvKey || AUTOMAPA_GA4_ENV_KEY,
        dataSource: {
          enabled: existingGa.dataSource?.enabled !== false,
          propertyIdEnvKey:
            existingGa.dataSource?.propertyIdEnvKey || "GA4_AUTOMAPA_PROPERTY_ID",
          pollIntervalMinutes: existingGa.dataSource?.pollIntervalMinutes || 60,
          syncLookbackDays: existingGa.dataSource?.syncLookbackDays || 7,
          ...(existingGa.dataSource || {}),
        },
      },
      tracking: {
        pageViews: true,
        ctaClicks: true,
        contactFormSubmissions: true,
        sourceAttribution: true,
        ...(existingAnalytics.tracking || {}),
      },
    },
    i18n: {
      ...mergedI18n,
      rollout,
    },
  };
}
